import { reactive } from "vue";
import { useUser } from "@domain/Security";
import { useNotify } from "@library/Components";
import { useDealerModeApi } from "@app/DealerMode/Types/Api/DealerModeApi";
import { DealerModeVehicle } from "@app/DealerMode/Types/DealerModeVehicle";
import type { IActivateVehicleModel } from "./IActivateVehicleModel";
import type { IDealerModeVehicle } from "@app/DealerMode/Types/IDealerModeVehicle";
import type { IDealerModeAppConfiguration } from "@domain/AppConfiguration/DealerModeAppConfiguration";

export const useViewModel = () => reactive( new class implements IActivateVehicleModel
{

    query?: string;
    appConfigurationId?: string;
    appName?: string;
    state: 'ready' | 'matched' | 'not-matched' | 'vehicle-activated' | 'already-activated' = 'ready';

    vehicle?: IDealerModeVehicle;
    results: IDealerModeVehicle[] = [];

    get isReady() { return this.state == 'ready' };
    get isMatched() { return this.state == 'matched' };
    get isNotMatched() { return this.state == 'not-matched' };
    get isActivated() { return this.state == 'vehicle-activated' };
    get isAlreadyActivated() { return this.state == 'already-activated' };

    constructor()
    {
    }

    /**
     * Resets the state of the form
     */
    public reset()
    {
        this.query = undefined;
        this.vehicle = undefined;
        this.state = 'ready';
    }

    /**
     * Attempts to find the vehicle from the rego and returns the vehicle if found
     */
    public async tryToFindVehicle()
    {

        if( ( this.query ?? '' ).length == 0 ) return;

        const api = useDealerModeApi();
        const user = useUser();

        const appConfigurationIds = user.roles.map(a => a.appIds).flat();
        const result = await api.findVehicleForActivation( this.query!, appConfigurationIds );
        const vehicle = result == null ? null : new DealerModeVehicle( result );

        if( vehicle )
        {
            this.vehicle = vehicle;
            this.state = vehicle.isActivated ? 'already-activated' : 'matched';
        }

        else
        {
            const api = useDealerModeApi();
            const getAvailableAppsResponse = await api.listAppConfigurationsAsync( user.roles.map( r => r.appIds ).flat() );
            const apps = getAvailableAppsResponse.data.appConfigurations;
            this.configureForNewVehicle( apps, this.query );
        }

    }

    /**
     * Configures the form to enter a new vehicle using a new vehicle model
     * inferred from the query
     * @param query
     */
    public configureForNewVehicle( apps: IDealerModeAppConfiguration[], query?: string )
    {
        this.vehicle = DealerModeVehicle.FromQuery( apps, query );
        this.state = 'not-matched';
    }

    /**
     * Activates or Creates a vehicle in the backend through the api
     * @param model
     */
    public async activateVehicle( model: IDealerModeVehicle )
    {

        const api = useDealerModeApi();
        const result = model.vehicleId == undefined
                     ? await api.createVehicle( model )
                     : await api.activateVehicle( model );

        if( result.success )
        {
            this.vehicle = new DealerModeVehicle( result.data.vehicle );
            this.state = 'vehicle-activated';
            return;
        }

        await useNotify( "Unfortunately the vehicle couldn't be activated.  Please check the details and try again", 'Activate vehicle' )

    }

} )
